import type { FC, ReactNode } from 'react'

import { AnimatePresence, motion } from 'motion/react'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { formatCapacity, formatInteger, formatPower } from '@/lib/inventory'
import { Kbd } from '@/components/datacenter/hud/primitives'
import CmsText from '@/components/datacenter/cms-text'

import {
    ArrowRight,
    DoorOpen,
    Loader2,
    MapPin,
    ShieldCheck
} from 'lucide-react'

const ease = [0.16, 1, 0.3, 1] as const

interface IntroOverlayProps {
    error: string | null
}

const IntroOverlay: FC<IntroOverlayProps> = ({ error }): ReactNode => {
    const phase = useDatacenterStore(state => state.phase)
    const ready = useDatacenterStore(state => state.sceneReady)
    const failed = useDatacenterStore(state => state.sceneFailed)
    const enter = useDatacenterStore(state => state.enter)
    const skipIntro = useDatacenterStore(state => state.skipIntro)
    const { inventory, stats } = useInventory()
    const { facility } = inventory
    const empty = inventory.racks.length === 0

    const figures = [
        { label: 'Racks', value: formatInteger(stats.racks) },
        { label: 'Devices', value: formatInteger(stats.devices) },
        { label: 'IT load', value: formatPower(stats.powerKw) },
        { label: 'Storage', value: formatCapacity(stats.storageTb) }
    ]

    return (
        <>
            <AnimatePresence>
                {phase === 'intro' && (
                    <motion.section
                        id='intro'
                        key='intro'
                        className='absolute inset-0 z-30 flex items-end bg-gradient-to-r from-[#03050a]/95 via-[#03050a]/70 to-transparent px-5 pb-8 sm:items-center sm:px-10 sm:pb-0 lg:px-16'
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.9, ease } }}
                        aria-labelledby='intro-title'
                    >
                        <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(34,211,238,0.12),transparent_55%)]' />

                        <motion.div
                            className='relative w-full max-w-xl'
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -40, filter: 'blur(8px)' }}
                            transition={{ duration: 0.9, ease }}
                        >
                            <div className='inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-300'>
                                <span className='relative flex size-2'>
                                    <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60' />
                                    <span className='relative inline-flex size-2 rounded-full bg-emerald-400' />
                                </span>
                                <span>
                                    Live inventory
                                </span>
                                {facility && (
                                    <>
                                        <span className='text-emerald-300/50' aria-hidden='true'>/</span>
                                        <CmsText table='facilities' id={facility.id} field='code' value={facility.code} className='font-mono tracking-wide' />
                                    </>
                                )}
                            </div>

                            <h1 id='intro-title' className='mt-5 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl'>
                                <span className='block text-slate-300'>
                                    Step inside
                                </span>
                                {facility ? (
                                    <CmsText
                                        table='facilities'
                                        id={facility.id}
                                        field='name'
                                        value={facility.name}
                                        className='block bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-300 bg-clip-text text-transparent'
                                    />
                                ) : (
                                    <span className='block bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-300 bg-clip-text text-transparent'>
                                        the data hall
                                    </span>
                                )}
                            </h1>

                            {empty ? (
                                <p className='mt-5 max-w-md text-base leading-relaxed text-slate-300'>
                                    No racks have been added yet. Once racks and devices are in the inventory, they appear here in 3D.
                                </p>
                            ) : facility?.description ? (
                                <CmsText
                                    as='p'
                                    table='facilities'
                                    id={facility.id}
                                    field='description'
                                    value={facility.description}
                                    className='mt-5 block max-w-md text-base leading-relaxed text-slate-300'
                                />
                            ) : null}

                            {facility && (facility.location || facility.tier) && (
                                <div className='mt-5 flex flex-wrap gap-2 text-xs text-slate-300'>
                                    {facility.location && (
                                        <span className='inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5'>
                                            <MapPin className='size-3.5 text-cyan-300' aria-hidden='true' />
                                            <CmsText table='facilities' id={facility.id} field='location' value={facility.location} />
                                        </span>
                                    )}
                                    {facility.tier && (
                                        <span className='inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5'>
                                            <ShieldCheck className='size-3.5 text-violet-300' aria-hidden='true' />
                                            <CmsText table='facilities' id={facility.id} field='tier' value={facility.tier} />
                                        </span>
                                    )}
                                </div>
                            )}

                            <dl className='mt-7 grid max-w-md grid-cols-4 gap-3 border-y border-white/10 py-4'>
                                {figures.map(figure => (
                                    <div key={figure.label}>
                                        <dt className='text-[11px] uppercase tracking-[0.14em] text-slate-500'>
                                            {figure.label}
                                        </dt>
                                        <dd className='mt-1 font-heading text-lg font-semibold text-white sm:text-xl'>
                                            {figure.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>

                            {failed && (
                                <p className='mt-4 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-sm text-amber-200' role='status'>
                                    This browser could not start 3D graphics, so the hall itself cannot be shown. The full inventory is still available.
                                </p>
                            )}

                            {error && (
                                <p className='mt-4 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-sm text-amber-200' role='status'>
                                    The live inventory could not be loaded just now, so the hall is shown empty. Refresh the page in a moment to try again.
                                </p>
                            )}

                            <div className='mt-7 flex flex-wrap items-center gap-4'>
                                <button
                                    type='button'
                                    onClick={failed ? skipIntro : enter}
                                    disabled={!ready && !failed}
                                    className='group relative inline-flex h-12 items-center gap-3 overflow-hidden rounded-full bg-cyan-300 pl-5 pr-4 text-sm font-semibold text-slate-950 shadow-[0_0_40px_-6px_rgba(34,211,238,0.8)] transition hover:bg-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-wait disabled:bg-slate-700 disabled:text-slate-300 disabled:shadow-none'
                                >
                                    <span className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 group-hover:translate-x-full' aria-hidden='true' />
                                    {ready || failed ? <DoorOpen className='relative size-5' aria-hidden='true' /> : <Loader2 className='relative size-5 animate-spin' aria-hidden='true' />}
                                    <span className='relative'>
                                        {failed ? 'Browse the inventory' : ready ? 'Enter the data hall' : 'Powering up the hall'}
                                    </span>
                                    {(ready || failed) && <ArrowRight className='relative size-4 transition-transform group-hover:translate-x-1' aria-hidden='true' />}
                                </button>

                                {!failed && (
                                    <button
                                        type='button'
                                        onClick={skipIntro}
                                        disabled={!ready}
                                        className='rounded-full px-2 py-1 text-sm text-slate-400 underline-offset-4 transition hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:opacity-40'
                                    >
                                        Skip the walk-in
                                    </button>
                                )}
                            </div>

                            <p className='mt-6 hidden items-center gap-2 text-xs text-slate-500 sm:flex'>
                                <span>
                                    Press
                                </span>
                                <Kbd>Enter</Kbd>
                                <span>
                                    to walk in. Drag to look around, scroll to move closer.
                                </span>
                            </p>
                        </motion.div>
                    </motion.section>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {phase === 'entering' && (
                    <motion.div key='letterbox' className='pointer-events-none absolute inset-0 z-30' initial='hidden' animate='visible' exit='hidden'>
                        <motion.div
                            className='absolute inset-x-0 top-0 h-[9vh] bg-black'
                            variants={{ hidden: { y: '-100%' }, visible: { y: 0 } }}
                            transition={{ duration: 0.8, ease }}
                        />
                        <motion.div
                            className='absolute inset-x-0 bottom-0 flex h-[9vh] items-center justify-center bg-black'
                            variants={{ hidden: { y: '100%' }, visible: { y: 0 } }}
                            transition={{ duration: 0.8, ease }}
                        >
                            <motion.p
                                className='flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-slate-400'
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { delay: 0.6 } }}
                            >
                                <span>
                                    Entering
                                </span>
                                {facility ? (
                                    <CmsText table='facilities' id={facility.id} field='name' value={facility.name} className='text-cyan-300' />
                                ) : (
                                    <span className='text-cyan-300'>
                                        the data hall
                                    </span>
                                )}
                            </motion.p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default IntroOverlay
